import { useState } from 'react';
import { Loader2, Copy, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SsoConfig, CreateSsoConfigInput, UpdateSsoConfigInput } from '@/api/hooks/useSso';

// The roles an SSO role claim may map to (the server's business roles — USER_ADMIN cluster, the SSO door).
const SSO_MAPPABLE_ROLES = ['member', 'analyst', 'executive_viewer', 'risk_manager'];

interface Props {
  existing?: SsoConfig;
  onSubmit: (data: CreateSsoConfigInput | UpdateSsoConfigInput) => void;
  onCancel: () => void;
  isPending: boolean;
}

type Protocol = 'oidc' | 'saml';
type SamlMetadataSource = 'url' | 'xml';

function ProtocolTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan'
          : 'border-border-subtle bg-bg-muted text-text-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors ml-1"
      title={`Copy ${label ?? 'value'}`}
    >
      {copied ? <CheckCheck className="h-3 w-3 text-status-success" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary pb-1 border-b border-border-subtle">
      {children}
    </p>
  );
}

export function SsoConfigForm({ existing, onSubmit, onCancel, isPending }: Props) {
  const lockedProtocol = existing?.protocol as Protocol | undefined;
  const [protocol, setProtocol] = useState<Protocol>(lockedProtocol ?? 'oidc');

  // OIDC state
  const [discoveryUrl, setDiscoveryUrl] = useState(
    (existing?.identity_provider_metadata.discovery_url as string) ?? '',
  );
  const [clientId, setClientId] = useState(
    (existing?.identity_provider_metadata.client_id as string) ?? '',
  );
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState(
    ((existing?.identity_provider_metadata.scopes as string[]) ?? ['openid', 'email', 'profile']).join(' '),
  );

  // SAML state
  const [idpEntityId, setIdpEntityId] = useState(
    (existing?.identity_provider_metadata.idp_entity_id as string) ?? '',
  );
  const [samlMetadataSource, setSamlMetadataSource] = useState<SamlMetadataSource>('url');
  const [idpMetadataUrl, setIdpMetadataUrl] = useState(
    (existing?.identity_provider_metadata.idp_metadata_url as string) ?? '',
  );
  const [idpMetadataXml, setIdpMetadataXml] = useState(
    (existing?.identity_provider_metadata.idp_metadata_xml as string) ?? '',
  );

  // Shared state
  const [jitEnabled, setJitEnabled] = useState(existing?.jit_provisioning_enabled ?? true);
  const [roleClaimName, setRoleClaimName] = useState(existing?.role_claim_name ?? '');
  const [roleClaimMappingRaw, setRoleClaimMappingRaw] = useState(
    existing?.role_claim_mapping ? JSON.stringify(existing.role_claim_mapping, null, 2) : '',
  );
  const [mappingError, setMappingError] = useState('');

  // Inline validation
  const idpEntityIdWarn = idpEntityId.includes('api.verigrc.com')
    ? 'This looks like the VeriGRC SP Entity ID — your IdP Entity ID should be provided by your identity provider, not VeriGRC.'
    : null;
  const roleClaimWarn = roleClaimName.trim().startsWith('{')
    ? 'This looks like a JSON object. The role claim name should be a plain attribute name (e.g. "groups"), not a JSON mapping.'
    : null;

  // SP values for copy buttons
  const spMetadataUrl = `${window.location.origin.replace('app.', 'api.')}/api/v1/auth/saml/metadata`;
  const oidcRedirectUri = `${window.location.origin.replace('app.', 'api.')}/api/v1/auth/oidc/callback`;

  function validateMapping(): Record<string, string> | null | undefined {
    if (!roleClaimMappingRaw.trim()) return null;
    try {
      const parsed = JSON.parse(roleClaimMappingRaw);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setMappingError('Must be a JSON object, e.g. {"Staff": "analyst"}');
        return undefined;
      }
      // A role claim maps to a business role only — super_admin and user_admin are granted in-app (the server refuses
      // anything else with 422; this says so before the round trip).
      const bad = Object.values(parsed).filter((v) => !SSO_MAPPABLE_ROLES.includes(v as string));
      if (bad.length > 0) {
        setMappingError(`A role claim may map only to ${SSO_MAPPABLE_ROLES.join(', ')} — not ${bad.join(', ')}`);
        return undefined;
      }
      setMappingError('');
      return parsed as Record<string, string>;
    } catch {
      setMappingError('Invalid JSON');
      return undefined;
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const mapping = validateMapping();
    if (mapping === undefined) return;

    const sharedFields = {
      jit_provisioning_enabled: jitEnabled,
      role_claim_name: roleClaimName.trim() || null,
      role_claim_mapping: mapping,
    };

    if (existing) {
      let meta: Record<string, unknown>;
      if (protocol === 'oidc') {
        meta = {
          discovery_url: discoveryUrl.trim(),
          client_id: clientId.trim(),
          scopes: scopes.trim().split(/\s+/).filter(Boolean),
        };
      } else {
        meta = { idp_entity_id: idpEntityId.trim() };
        if (samlMetadataSource === 'url' && idpMetadataUrl.trim()) {
          meta.idp_metadata_url = idpMetadataUrl.trim();
        } else if (samlMetadataSource === 'xml' && idpMetadataXml.trim()) {
          meta.idp_metadata_xml = idpMetadataXml.trim();
        }
      }
      const update: UpdateSsoConfigInput = {
        identity_provider_metadata: meta,
        ...sharedFields,
      };
      if (protocol === 'oidc' && clientSecret) update.client_secret = clientSecret;
      onSubmit(update);
    } else {
      if (protocol === 'oidc') {
        if (!clientSecret) return;
        onSubmit({
          protocol: 'oidc',
          identity_provider_metadata: {
            discovery_url: discoveryUrl.trim(),
            client_id: clientId.trim(),
            scopes: scopes.trim().split(/\s+/).filter(Boolean),
          },
          client_secret: clientSecret,
          ...sharedFields,
        });
      } else {
        const meta: { idp_entity_id: string; idp_metadata_url?: string; idp_metadata_xml?: string } = {
          idp_entity_id: idpEntityId.trim(),
        };
        if (samlMetadataSource === 'url') {
          meta.idp_metadata_url = idpMetadataUrl.trim();
        } else {
          meta.idp_metadata_xml = idpMetadataXml.trim();
        }
        onSubmit({
          protocol: 'saml',
          identity_provider_metadata: meta,
          ...sharedFields,
        });
      }
    }
  }

  const samlMetaValid =
    idpEntityId.trim() &&
    (samlMetadataSource === 'url' ? idpMetadataUrl.trim() : idpMetadataXml.trim());

  const oidcSubmitDisabled =
    !discoveryUrl || !clientId || (!existing && !clientSecret);

  const samlSubmitDisabled = !samlMetaValid;

  const submitDisabled =
    isPending || (protocol === 'oidc' ? oidcSubmitDisabled : samlSubmitDisabled);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Protocol selector — locked when editing */}
      <div className="space-y-1.5">
        <Label>Protocol</Label>
        {lockedProtocol ? (
          <div className="rounded-lg border border-border-subtle bg-bg-muted px-3 py-2 text-sm font-medium text-text-primary">
            {lockedProtocol === 'oidc' ? 'OpenID Connect (OIDC)' : 'SAML 2.0'}
          </div>
        ) : (
          <div className="flex gap-2">
            <ProtocolTab active={protocol === 'oidc'} onClick={() => setProtocol('oidc')}>
              OpenID Connect (OIDC)
            </ProtocolTab>
            <ProtocolTab active={protocol === 'saml'} onClick={() => setProtocol('saml')}>
              SAML 2.0
            </ProtocolTab>
          </div>
        )}
      </div>

      {/* ── OIDC fields ─────────────────────────────────────────────────── */}
      {protocol === 'oidc' && (
        <>
          <SectionHeader>What your identity provider gives you</SectionHeader>

          <div className="space-y-1.5">
            <Label htmlFor="discovery_url">
              Identity Provider URL <span className="text-xs font-normal text-text-secondary ml-1">(Discovery URL)</span>
            </Label>
            <Input
              id="discovery_url"
              type="url"
              placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
              value={discoveryUrl}
              onChange={(e) => setDiscoveryUrl(e.target.value)}
              required
            />
            <p className="text-xs text-text-secondary">
              Your IdP's OpenID Connect issuer URL. In Azure Entra: <em>Overview → Endpoints → OpenID Connect metadata document</em> (strip the <code>/.well-known/…</code> suffix). In Okta: the Issuer field on your Authorization Server.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="client_id">
              Application ID <span className="text-xs font-normal text-text-secondary ml-1">(Client ID)</span>
            </Label>
            <Input
              id="client_id"
              type="text"
              placeholder="Application (client) ID from your IdP"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
            />
            <p className="text-xs text-text-secondary">
              The unique identifier your IdP issued for this application registration. In Azure Entra: <em>App registrations → your app → Application (client) ID</em>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="client_secret">
              Client Secret{existing && <span className="ml-1 text-xs text-text-secondary">(leave blank to keep existing)</span>}
            </Label>
            <Input
              id="client_secret"
              type="password"
              placeholder={existing ? '••••••••  (write-only — re-enter to rotate)' : 'Paste client secret from your IdP'}
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              required={!existing}
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scopes">Permission Scopes</Label>
            <Input
              id="scopes"
              type="text"
              placeholder="openid email profile"
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
              required
            />
            <p className="text-xs text-text-secondary">
              Space-separated list. <code>openid email profile</code> are required. Add <code>groups</code> to enable role-claim mapping below.
            </p>
          </div>

          <SectionHeader>What you register at your identity provider</SectionHeader>

          <div className="rounded-lg border border-border-subtle bg-bg-muted p-3 text-xs text-text-secondary space-y-2">
            <div>
              <span className="font-medium text-text-primary">Redirect URI (callback URL)</span>
              <div className="flex items-center gap-1 mt-1">
                <code className="rounded bg-bg-surface px-1 py-0.5 font-mono break-all">{oidcRedirectUri}</code>
                <CopyButton value={oidcRedirectUri} label="Redirect URI" />
              </div>
              <p className="mt-1">Add this exact URL as an allowed redirect URI in your IdP application registration.</p>
            </div>
          </div>
        </>
      )}

      {/* ── SAML fields ─────────────────────────────────────────────────── */}
      {protocol === 'saml' && (
        <>
          <SectionHeader>What your identity provider gives you</SectionHeader>

          <div className="space-y-1.5">
            <Label htmlFor="idp_entity_id">
              IdP Issuer / Entity ID <span className="text-xs font-normal text-text-secondary ml-1">(IdP Entity ID)</span>
            </Label>
            <Input
              id="idp_entity_id"
              type="text"
              placeholder="https://sts.windows.net/{tenant-id}/"
              value={idpEntityId}
              onChange={(e) => setIdpEntityId(e.target.value)}
              required
            />
            {idpEntityIdWarn && (
              <p className="text-xs text-status-warning">{idpEntityIdWarn}</p>
            )}
            <p className="text-xs text-text-secondary">
              The entityID from your IdP's SAML metadata — this identifies your IdP, not VeriGRC. In Azure Entra: <em>Enterprise apps → your app → SAML → Basic SAML Configuration → Identifier (Entity ID)</em>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>IdP Metadata</Label>
            <div className="flex gap-2 text-sm">
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name="saml_metadata_source"
                  value="url"
                  checked={samlMetadataSource === 'url'}
                  onChange={() => setSamlMetadataSource('url')}
                  className="accent-accent-cyan"
                />
                Metadata URL (recommended)
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="radio"
                  name="saml_metadata_source"
                  value="xml"
                  checked={samlMetadataSource === 'xml'}
                  onChange={() => setSamlMetadataSource('xml')}
                  className="accent-accent-cyan"
                />
                Paste XML
              </label>
            </div>

            {samlMetadataSource === 'url' ? (
              <div className="space-y-1">
                <Input
                  id="idp_metadata_url"
                  type="url"
                  placeholder="https://login.microsoftonline.com/{tenant}/federationmetadata/2007-06/federationmetadata.xml"
                  value={idpMetadataUrl}
                  onChange={(e) => setIdpMetadataUrl(e.target.value)}
                  required={samlMetadataSource === 'url' && !existing}
                />
                <p className="text-xs text-text-secondary">
                  Federation metadata URL from your IdP. Cached for 24 h; refreshed on metadata test. In Azure Entra: <em>Enterprise apps → your app → SAML → SAML Certificates → App Federation Metadata URL</em>.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <textarea
                  id="idp_metadata_xml"
                  rows={8}
                  className="w-full rounded-lg border border-border-subtle bg-bg-muted px-3 py-2 font-mono text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
                  placeholder={'<?xml version="1.0"?>\n<EntityDescriptor ...>...</EntityDescriptor>'}
                  value={idpMetadataXml}
                  onChange={(e) => setIdpMetadataXml(e.target.value)}
                  required={samlMetadataSource === 'xml' && !existing}
                />
                <p className="text-xs text-text-secondary">
                  Paste the full SAML metadata XML from your IdP. Stored verbatim — update manually when certificates rotate.
                </p>
              </div>
            )}
          </div>

          <SectionHeader>What you register at your identity provider</SectionHeader>

          <div className="rounded-lg border border-border-subtle bg-bg-muted p-3 text-xs text-text-secondary space-y-3">
            <div>
              <span className="font-medium text-text-primary">SP Metadata URL</span>
              <div className="flex items-center gap-1 mt-1">
                <code className="rounded bg-bg-surface px-1 py-0.5 font-mono break-all">{spMetadataUrl}</code>
                <CopyButton value={spMetadataUrl} label="SP Metadata URL" />
              </div>
              <p className="mt-1">Point your IdP to this URL to auto-import the SP EntityDescriptor, signing certificate, and ACS URL. Preferred over manual entry.</p>
            </div>
            <div>
              <span className="font-medium text-text-primary">Assertion Consumer Service (ACS) URL</span>
              <div className="flex items-center gap-1 mt-1">
                <code className="rounded bg-bg-surface px-1 py-0.5 font-mono break-all">
                  {spMetadataUrl.replace('/metadata', '/callback')}
                </code>
                <CopyButton value={spMetadataUrl.replace('/metadata', '/callback')} label="ACS URL" />
              </div>
              <p className="mt-1">HTTP-POST binding. Use this if your IdP requires the ACS URL separately instead of importing SP metadata.</p>
            </div>
          </div>
        </>
      )}

      {/* ── Shared fields ────────────────────────────────────────────────── */}
      <SectionHeader>Provisioning and role mapping</SectionHeader>

      <div className="flex items-center gap-3">
        <input
          id="jit_enabled"
          type="checkbox"
          checked={jitEnabled}
          onChange={(e) => setJitEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-border-subtle accent-accent-cyan"
        />
        <Label htmlFor="jit_enabled" className="cursor-pointer">
          Auto-create accounts on first SSO login <span className="text-xs font-normal text-text-secondary">(JIT provisioning)</span>
        </Label>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="role_claim_name">
          Role attribute name <span className="text-text-secondary text-xs font-normal">(optional)</span>
        </Label>
        <Input
          id="role_claim_name"
          type="text"
          placeholder={protocol === 'saml' ? 'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups' : 'groups'}
          value={roleClaimName}
          onChange={(e) => setRoleClaimName(e.target.value)}
        />
        {roleClaimWarn && (
          <p className="text-xs text-status-warning">{roleClaimWarn}</p>
        )}
        <p className="text-xs text-text-secondary">
          {protocol === 'saml'
            ? 'The SAML attribute name your IdP uses to send group or role values. Multi-valued attributes use first-match-wins.'
            : 'The JWT claim name your IdP uses to send group or role values. Leave blank to skip role mapping.'}
          {!roleClaimName && (
            <> Fill in this field to configure how IdP roles map to VeriGRC roles.</>
          )}
        </p>
      </div>

      {roleClaimName && (
        <div className="space-y-1.5">
          <Label htmlFor="role_claim_mapping">Role mapping <span className="text-text-secondary text-xs font-normal">(JSON)</span></Label>
          <textarea
            id="role_claim_mapping"
            rows={4}
            className="w-full rounded-lg border border-border-subtle bg-bg-muted px-3 py-2 font-mono text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
            placeholder={'{\n  "idp-admin-group": "super_admin",\n  "idp-editor-group": "risk_manager"\n}'}
            value={roleClaimMappingRaw}
            onChange={(e) => { setRoleClaimMappingRaw(e.target.value); setMappingError(''); }}
          />
          {mappingError && <p className="text-xs text-status-error">{mappingError}</p>}
          <p className="text-xs text-text-secondary">
            Map IdP claim values to VeriGRC roles: <code>super_admin</code>, <code>risk_manager</code>, <code>analyst</code>, <code>member</code>.
            Unmapped values preserve the user's existing role (or <code>member</code> for new JIT users).
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={submitDisabled}>
          {isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : existing ? 'Save changes' : 'Create configuration'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
